import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {Menu, MenuItem} from "@mui/material";
import {styled} from "@mui/material/styles";

import {ReactComponent as Sort} from "assets/img/sort-linear.svg";
import {black500, grey300, greyishBlue50, greyishBlue200} from "assets/jss/main";

CatalogSummaryPeriodSelect.propTypes = {
    // one of PERIODS below
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

/**
 * The comparison windows the endpoint accepts. Keys are sent verbatim as the `period`
 * query parameter, so they must stay in step with RetailerProductSnapshotRepository::PERIODS.
 */
export const PERIODS = [
    {value: 'week', labelId: 'retailer.product.catalogSummary.period.week'},
    {value: 'month', labelId: 'retailer.product.catalogSummary.period.month'},
    {value: 'quarter', labelId: 'retailer.product.catalogSummary.period.quarter'},
    {value: 'year', labelId: 'retailer.product.catalogSummary.period.year'},
];

export const periodLabelId = (value) =>
    (PERIODS.find((period) => period.value === value) ?? PERIODS[0]).labelId;

// Grey, borderless: the DS gives this dropdown a lighter weight than the blue sort button
// of the table toolbar, because it changes a reading window rather than the data.
const StyledButton = styled('button')({
    height: '40px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    flexShrink: 0,
    padding: '12px 16px',
    backgroundColor: grey300,
    border: 'none',
    borderRadius: '8px',
    color: black500,
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: greyishBlue50
    },
    'svg': {
        width: '16px',
        height: '16px'
    },
    'svg path': {
        stroke: black500
    }
});

const menuPaperSx = {
    marginTop: '4px',
    borderRadius: '8px',
    border: `1px solid ${greyishBlue200}`,
    boxShadow: '0px 4px 22px 0px rgba(0, 0, 0, 0.04)',
    padding: '12px 8px',
    '& .MuiList-root': {
        padding: 'unset'
    },
    '& .MuiMenuItem-root': {
        height: '32px',
        padding: '8px',
        borderRadius: '8px',
        color: black500,
        fontSize: '14px',
        fontWeight: 400,
        '&:hover': {
            backgroundColor: greyishBlue50
        }
    }
};

function CatalogSummaryPeriodSelect({value, onChange}) {
    const [anchorEl, setAnchorEl] = useState(null);

    const close = () => setAnchorEl(null);

    const select = (period) => {
        close();
        if (period !== value) {
            onChange(period);
        }
    };

    return (
        <>
            <StyledButton type="button" onClick={(event) => setAnchorEl(event.currentTarget)}>
                <FormattedMessage id={periodLabelId(value)}/>
                <Sort/>
            </StyledButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={close}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}
                PaperProps={{sx: menuPaperSx}}
            >
                {PERIODS.map((period) => (
                    <MenuItem
                        key={period.value}
                        selected={period.value === value}
                        onClick={() => select(period.value)}
                    >
                        <FormattedMessage id={period.labelId}/>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default CatalogSummaryPeriodSelect;
